import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { searchCustomers } from '../services/customerService';
import { createGroup, updateGroup } from '../services/groupService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import styles from './AddGroup.module.css';

const AddGroup = ({ isOpen, onClose, onSave, group = null }) => {
    const dispatch = useDispatch();
    const dialogRef = useRef(null);
    const [groupName, setGroupName] = useState('');
    const [swagId, setSwagId] = useState('');
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Pre-populate data if editing existing group
    useEffect(() => {
        if (group) {
            setGroupName(group.name || '');
            setSwagId(group.swag_id || '');
            setSelectedCustomers(group.users || []);
        } else {
            // Reset form for new group
            setGroupName('');
            setSwagId('');
            setSelectedCustomers([]);
        }
        setSearchQuery('');
        setSearchResults([]);
    }, [group, isOpen]);

    // Search customers when search query changes
    useEffect(() => {
        const searchCustomersAPI = async () => {
            if (searchQuery.trim().length > 0) {
                setSearchLoading(true);
                try {
                    const response = await searchCustomers(searchQuery);
                    if (response.data && Array.isArray(response.data)) {
                        // Filter out already selected customers
                        const filteredResults = response.data.filter(customer =>
                            !selectedCustomers.some(selected => selected.user_id === customer._id)
                        );
                        setSearchResults(filteredResults);
                    } else {
                        setSearchResults([]);
                    }
                } catch (error) {
                    console.error('Failed to search customers:', error);
                    setSearchResults([]);
                } finally {
                    setSearchLoading(false);
                }
            } else {
                setSearchResults([]);
            }
        };

        const timeoutId = setTimeout(searchCustomersAPI, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery, selectedCustomers]);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Handle click outside dialog
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleCustomerSelect = (customer) => {
        const customerData = {
            user_id: customer._id,
            name: customer.name,
            email: customer.email
        };

        setSelectedCustomers(prev => [...prev, customerData]);
        setSearchQuery('');
        setSearchResults([]);
        setIsDropdownOpen(false);
    };

    const handleRemoveCustomer = (userId) => {
        setSelectedCustomers(prev => prev.filter(customer => customer.user_id !== userId));
    };

    const handleSave = async () => {
        if (!groupName.trim() || !swagId.trim()) {
            dispatch(showErrorNotification('Group name and SWAG ID are required'));
            return;
        }

        setSaveLoading(true);
        try {
            const groupData = {
                name: groupName.trim(),
                swag_id: swagId.trim(),
                user_ids: selectedCustomers.map(customer => customer.user_id)
            };

            let response;
            if (group) {
                // Update existing group
                response = await updateGroup(group._id, groupData);
                dispatch(showSuccessNotification('Group updated successfully'));
            } else {
                // Create new group
                response = await createGroup(groupData);
                dispatch(showSuccessNotification('Group created successfully'));
            }

            onSave(response);
            onClose();
        } catch (error) {
            console.error('Failed to save group:', error);
            dispatch(showErrorNotification(error.message || 'Failed to save group'));
        } finally {
            setSaveLoading(false);
        }
    };

    const isFormValid = () => {
        return groupName.trim() && swagId.trim();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.dialog} ref={dialogRef}>
                <div className={styles.header}>
                    <h2>{group ? 'Edit Group' : 'Create New Group'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className={styles.content}>
                    <div className={styles.formGroup}>
                        <label>Group Name *</label>
                        <input
                            type="text"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            placeholder="Enter group name"
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>SWAG ID *</label>
                        <input
                            type="text"
                            value={swagId}
                            onChange={(e) => setSwagId(e.target.value)}
                            placeholder="Enter SWAG ID"
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Add Customers</label>
                        <div className={styles.dropdownContainer} ref={dropdownRef}>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setIsDropdownOpen(true);
                                }}
                                onFocus={() => setIsDropdownOpen(true)}
                                placeholder="Search customers by SWAG ID..."
                                className={styles.searchInput}
                            />

                            {isDropdownOpen && (
                                <div className={styles.dropdown}>
                                    {searchLoading ? (
                                        <div className={styles.loadingMessage}>Searching...</div>
                                    ) : searchResults.length > 0 ? (
                                        searchResults.map(customer => (
                                            <div
                                                key={customer._id}
                                                className={styles.dropdownOption}
                                                onClick={() => handleCustomerSelect(customer)}
                                            >
                                                <div className={styles.customerAvatar}>
                                                    {customer.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className={styles.customerInfo}>
                                                    <div className={styles.customerName}>{customer.name}</div>
                                                    <div className={styles.customerEmail}>{customer.email}</div>
                                                </div>
                                            </div>
                                        ))
                                    ) : searchQuery.trim() ? (
                                        <div className={styles.noResults}>No customers found</div>
                                    ) : (
                                        <div className={styles.noResults}>Start typing to search customers</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedCustomers.length > 0 && (
                        <div className={styles.selectedCustomers}>
                            <label>Selected Customers ({selectedCustomers.length})</label>
                            <div className={styles.customersList}>
                                {selectedCustomers.map(customer => (
                                    <div key={customer.user_id} className={styles.selectedCustomer}>
                                        <div className={styles.customerAvatar}>
                                            {customer.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className={styles.customerInfo}>
                                            <div className={styles.customerName}>{customer.name}</div>
                                            <div className={styles.customerEmail}>{customer.email}</div>
                                        </div>
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => handleRemoveCustomer(customer.user_id)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.actions}>
                    <button className={styles.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={`${styles.saveButton} ${!isFormValid() || saveLoading ? styles.disabled : ''}`}
                        onClick={handleSave}
                        disabled={!isFormValid() || saveLoading}
                    >
                        {saveLoading ? (
                            <>
                                <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                                </svg>
                                {group ? 'Updating...' : 'Creating...'}
                            </>
                        ) : (
                            group ? 'Update Group' : 'Create Group'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddGroup;